import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CheckCircle, Star, AlertCircle, ChevronRight, ChevronLeft, Camera, X, Loader2 } from 'lucide-react';

import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Textarea } from '../../../components/ui/textarea';
import LanguageGate, { useLanguageGate, ChangeLanguageLink, ENABLED_LANGUAGES_FALLBACK } from '../../../components/shared/LanguageGate';
import { cardSurfaceClass } from '../../../components/shared/cardSurface';
import SurveyGiftFlow from '../components/SurveyGiftFlow';
import BrandingShell from '../../../components/shared/BrandingShell';

import { surveysAPI } from '../api';


/**
 * Public respondent page. Mounted at /s/:slug — NO auth, NO sidebar.
 * Mobile-first (390px). Survey questions render in user's browser language.
 *
 * P_MULTIPLE_CHOICE_BRANCHING_SURVEYS_1.0
 * --------------------------------------------------------------------
 * If the survey contains AT LEAST ONE question with
 * `response_type === 'multiple_choice_branching'`, we switch to
 * **step-by-step mode**: one question per screen, branching answers
 * auto-advance, "End" submits everything collected. Skipped questions
 * are NEVER rendered.
 *
 * If the survey has zero branching questions, we keep the legacy
 * single-page scrolling layout. This guarantees 100% backward
 * compatibility with every existing survey.
 */
const LOGO_SIZE_CLASSES = {
    small: 'max-h-16 max-w-[120px]',
    medium: 'max-h-20 max-w-[180px]',
    large: 'max-h-32 max-w-[280px]',
    very_large: 'max-h-44 max-w-[360px]',
};

const SurveyPublic = () => {
    const { slug } = useParams();
    const { t, i18n } = useTranslation();
    const [survey, setSurvey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [answers, setAnswers] = useState({});
    const [notes, setNotes] = useState('');
    const [submitted, setSubmitted] = useState(false);
    // P_SURVEY_GIFT_1.0 — the saved response id drives the one-gift-per-
    // submission rule; `giftSkipped` falls back to the normal thank-you.
    const [responseId, setResponseId] = useState(null);
    const [giftSkipped, setGiftSkipped] = useState(false);
    const [reward, setReward] = useState(null);
    // "Other" choices: which one is open, and the words typed per choice
    // (kept on back navigation).
    const [otherPick, setOtherPick] = useState(null);
    const [otherTexts, setOtherTexts] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [photo, setPhoto] = useState(null);      // {id, previewUrl}
    const [photoUploading, setPhotoUploading] = useState(false);

    // Step-by-step navigation. The visited path is a stack of
    // question indices so we can prevent infinite loops if an author
    // configures a jump that would re-enter an already-shown question
    // on the same path (we just submit at that point).
    // Final confirm step (reward surveys only) — holds the answers until
    // the guest presses Finish.
    const [pendingAnswers, setPendingAnswers] = useState(null);
    const [currentIdx, setCurrentIdx] = useState(0);
    const [visitedPath, setVisitedPath] = useState([0]);

    // P_SURVEY_EDITOR_UX_ENHANCEMENTS_1.0 — platform-level initial
    // "Overall Experience" 5-star screen. Shown only when the survey
    // has `overall_experience_enabled !== false` (default true). The
    // rating is stored as a regular ResponseAnswer with the reserved
    // question_id `__overall_experience__` so analytics/Review Shield
    // /sentiment paths see it just like any other rating answer.
    const OVERALL_QID = '__overall_experience__';
    const [overallDone, setOverallDone] = useState(false);

    const lang = i18n.language || 'en';
    const enabledLangs = survey?.enabled_languages || ENABLED_LANGUAGES_FALLBACK;
    const { language: chosenLang, needsPicker, choose, reset: resetLang } = useLanguageGate(
        survey ? enabledLangs : null,
        `s:${slug}`
    );

    useEffect(() => {
        surveysAPI.publicGet(slug)
            .then(s => setSurvey(s))
            .catch(err => setError(err?.response?.data?.detail || 'Survey unavailable'))
            .finally(() => setLoading(false));
    }, [slug]);

    // v3.5.0 — Force display mode (light/dark/auto) on <html> before render.
    useEffect(() => {
        const mode = survey?.display_mode;
        const html = document.documentElement;
        const prevDark = html.classList.contains('dark');
        const prevForced = html.dataset.surveyForcedMode || '';

        if (mode === 'dark') {
            html.classList.add('dark');
            html.dataset.surveyForcedMode = 'dark';
        } else if (mode === 'light') {
            html.classList.remove('dark');
            html.dataset.surveyForcedMode = 'light';
        } else {
            const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
            if (prefersDark) html.classList.add('dark');
            else html.classList.remove('dark');
            delete html.dataset.surveyForcedMode;
        }
        return () => {
            if (prevForced) html.dataset.surveyForcedMode = prevForced;
            else delete html.dataset.surveyForcedMode;
            if (prevDark) html.classList.add('dark');
            else html.classList.remove('dark');
        };
    }, [survey?.display_mode]);

    const questions = survey?.questions || [];
    // Step-by-step mode is enabled if ANY question is branching. Memo
    // so the flag is stable across renders.
    const branchingMode = useMemo(
        () => questions.some((q) => q.response_type === 'multiple_choice_branching'),
        [questions]
    );

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center p-6 text-muted-foreground">{t('common.loading')}</div>;
    }
    if (error || !survey) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6">
                <Card className="max-w-md w-full">
                    <CardContent className="pt-6 text-center space-y-3">
                        <AlertCircle className="w-10 h-10 text-amber-500 mx-auto" />
                        <p className="font-medium">{t('surveys.publicUnavailableTitle')}</p>
                        <p className="text-sm text-muted-foreground">{error || t('surveys.publicUnavailableDesc')}</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const effectiveLogoUrl = survey.effective_logo_url || survey.logo_url || null;
    const effectiveWallpaperUrl = survey.effective_background_image_url || survey.background_image_url || null;
    const effectiveCardStyle = survey.effective_card_style || 'frosted';
    const shellVariant = survey.display_mode === 'dark' ? 'dark' : 'light';
    // P_SURVEY_CARD_STYLE_PREVIEW_1.0 — the question screens now use the same
    // surface as the language gate, so the admin's choice is visible on every
    // screen of the survey, not just the gate.
    const questionCardSurface = cardSurfaceClass(effectiveCardStyle, shellVariant);

    if (needsPicker) {
        return (
            <BrandingShell wallpaperUrl={effectiveWallpaperUrl} variant={shellVariant} data-testid="survey-public-root">
                <LanguageGate
                    enabled={enabledLangs}
                    onSelect={choose}
                    variant={shellVariant === 'dark' ? 'dark' : 'light'}
                    logoUrl={effectiveLogoUrl}
                    logoSize={survey.logo_size || 'medium'}
                    embedded
                    cardStyle={effectiveCardStyle}
                />
            </BrandingShell>
        );
    }
    const effectiveLang = chosenLang || lang || 'en';

    if (submitted) {
        // P_SURVEY_CLOSING_PAGE_1.1 — configurable closing page.
        // When `closing_page.enabled === true`, render the author's
        // copy + optional CTA + optional Google Review button. When
        // disabled, fall back to the legacy "Thank you" screen so
        // every existing survey keeps the exact same UX.
        const cp = survey.closing_page;
        const cpEnabled = !!(cp && cp.enabled);
        const closingTitle = cpEnabled && (cp.title?.[effectiveLang] || cp.title?.en);
        const closingMessage = cpEnabled && (cp.message?.[effectiveLang] || cp.message?.en);
        const actionLabel = cpEnabled && cp.action_button?.label
            ? (cp.action_button.label[effectiveLang] || cp.action_button.label.en)
            : '';
        const actionUrl = cpEnabled ? (cp.action_button?.url || '') : '';
        const googleUrl = cpEnabled && cp.google_review_enabled
            ? (cp.resolved_google_review_url || '')
            : '';
        // Schedule the optional auto-redirect once on first render of
        // the closing screen.
        if (cpEnabled && cp.redirect_url && !window.__yot_redir_scheduled) {
            window.__yot_redir_scheduled = true;
            setTimeout(() => {
                try { window.location.href = cp.redirect_url; } catch { /* ignore */ }
            }, 5000);
        }
        // P_SURVEY_GIFT_1.0 — "A gift from us" takes the closing screen when
        // the survey offers it and the total limit has not been reached. At the
        // limit, or once the respondent skips, the normal thank-you renders.
        const giftCfg = (cp && cp.gift) || null;
        const giftLeft = giftCfg
            ? Math.max(0, (giftCfg.total_limit || 0) - (giftCfg.issued_count || 0))
            : 0;
        // An unticked box is stored as false; a key the editor never wrote is
        // ABSENT and still means "offered", exactly as the editor displays it.
        const giftOn = !!(giftCfg && giftCfg.enabled && giftLeft > 0 && responseId && !giftSkipped
            && (giftCfg.allow_self !== false || giftCfg.allow_friend !== false
                || giftCfg.allow_both !== false));
        if (giftOn) {
            return (
                <BrandingShell wallpaperUrl={effectiveWallpaperUrl} variant={shellVariant} data-testid="survey-public-root">
                    <Card className={`max-w-md w-full mx-auto ${questionCardSurface}`} data-card-style={effectiveCardStyle} data-testid="survey-gift-card">
                        <CardContent className="pt-8 pb-8">
                            <SurveyGiftFlow
                                slug={slug}
                                responseId={responseId}
                                config={giftCfg}
                                language={effectiveLang}
                                onSkip={() => setGiftSkipped(true)}
                            />
                        </CardContent>
                    </Card>
                </BrandingShell>
            );
        }
        return (
            <BrandingShell wallpaperUrl={effectiveWallpaperUrl} variant={shellVariant} data-testid="survey-public-root">
                <Card className="max-w-md w-full mx-auto" data-testid="survey-closing-card">
                    <CardContent className="pt-8 pb-8 text-center space-y-4">
                        {cpEnabled && cp.image_url ? (
                            <img
                                src={cp.image_url}
                                alt=""
                                className="max-h-24 mx-auto object-contain"
                                data-testid="closing-page-image"
                            />
                        ) : (
                            <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center mx-auto">
                                <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
                            </div>
                        )}
                        <p className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="survey-thank-you-title">
                            {closingTitle || t('surveys.thankYou')}
                        </p>
                        <p className="text-sm text-muted-foreground" data-testid="survey-thank-you-desc">
                            {closingMessage || t('surveys.thankYouDesc')}
                        </p>

                        {/* Scratch & Win — shown on the closing page AND on the
                            legacy thank-you screen, whenever the response
                            earned a scratch. */}
                        {reward && (
                            <a
                                href={`/demo/${encodeURIComponent(reward.cardId)}?reward_token=${encodeURIComponent(reward.token)}`}
                                className="inline-flex items-center justify-center w-full h-11 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-base font-semibold transition-colors"
                                data-testid="survey-scratch-win-btn"
                            >
                                {t('surveys.closing.scratchWinBtn', 'Scratch & Win')}
                            </a>
                        )}

                        {/* Optional CTA button */}
                        {cpEnabled && actionUrl && actionLabel && (
                            <a
                                href={actionUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center w-full h-10 rounded-md bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-sm font-medium transition-colors"
                                data-testid="closing-page-action-btn"
                            >
                                {actionLabel}
                            </a>
                        )}

                        {/* Google Review CTA — opt-in + resolves to the
                            survey's location google_reviews URL */}
                        {cpEnabled && googleUrl && (
                            <div className="space-y-2 pt-2">
                                <p className="text-sm text-muted-foreground">
                                    {t('surveys.closing.googleReviewPrompt', 'Would you like to share your experience on Google?')}
                                </p>
                                <a
                                    href={googleUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center w-full h-10 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors"
                                    data-testid="closing-page-google-review-btn"
                                >
                                    {t('surveys.closing.googleReviewBtn', 'Leave a Google Review')}
                                </a>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </BrandingShell>
        );
    }

    // ── Submit helper ────────────────────────────────────────
    const submitAll = async (collected) => {
        setSubmitting(true);
        try {
            const res = await surveysAPI.publicSubmit(slug, {
                answers: Object.values(collected),
                notes: notes.trim() || null,
                language: chosenLang || lang,
                photos: photo?.id ? [photo.id] : undefined,
            });
            // P_SURVEY_SCRATCH_WIN_1.0 — a completed response can earn one
            // scratch on the survey's reward card.
            if (res?.id) setResponseId(res.id);
            if (res?.reward_token && res?.reward_card_id) {
                setReward({ token: res.reward_token, cardId: res.reward_card_id });
            }
            setSubmitted(true);
        } catch (err) {
            setError(err?.response?.data?.detail || t('common.saveFailed'));
        } finally {
            setSubmitting(false);
        }
    };

    // ── Optional respondent photo (camera or library) ────────
    const handlePhotoSelect = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';  // allow re-selecting the same file
        if (!file) return;
        if (!file.type?.startsWith('image/')) {
            setError(t('surveys.photoInvalid', 'Please choose an image file'));
            return;
        }
        setPhotoUploading(true);
        try {
            const res = await surveysAPI.publicUploadPhoto(slug, file);
            if (photo?.previewUrl) URL.revokeObjectURL(photo.previewUrl);
            setPhoto({ id: res.id, previewUrl: URL.createObjectURL(file) });
        } catch (err) {
            setError(err?.response?.data?.detail || t('surveys.photoUploadFailed', 'Photo upload failed'));
        } finally {
            setPhotoUploading(false);
        }
    };
    const removePhoto = () => {
        if (photo?.previewUrl) URL.revokeObjectURL(photo.previewUrl);
        setPhoto(null);
    };

    const PhotoControl = () => (
        <div className="space-y-2" data-testid="public-photo-control">
            <p className="text-sm font-medium">{t('surveys.addPhotoLabel', 'Add a photo (optional)')}</p>
            {photo ? (
                <div className="relative inline-block">
                    <img src={photo.previewUrl} alt="" className="max-h-40 rounded-lg border" data-testid="public-photo-preview" />
                    <button
                        type="button"
                        onClick={removePhoto}
                        className="absolute -top-2 -right-2 bg-slate-900 text-white rounded-full p-1 shadow"
                        aria-label={t('common.remove', 'Remove')}
                        data-testid="public-photo-remove"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            ) : (
                <div className="flex flex-wrap gap-2">
                    <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm hover:bg-slate-50 dark:hover:bg-slate-800" data-testid="public-photo-camera">
                        {photoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                        {t('surveys.takePhoto', 'Take a photo')}
                        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoSelect} disabled={photoUploading} data-testid="public-photo-camera-input" />
                    </label>
                    <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm hover:bg-slate-50 dark:hover:bg-slate-800" data-testid="public-photo-library">
                        {t('surveys.choosePhoto', 'Choose a photo')}
                        <input type="file" accept="image/*" className="hidden" onChange={handlePhotoSelect} disabled={photoUploading} data-testid="public-photo-library-input" />
                    </label>
                </div>
            )}
        </div>
    );

    // ── Path engine ──────────────────────────────────────────
    // Given the current question index and an answer record, decide
    // what to do next.
    //
    // P_BRANCHING_TREE_ENGINE_1.2 — every question (NOT just branching
    // questions) now has its own optional `next_step`. The previous
    // implementation always linear-advanced after a non-branching
    // answer, which caused a respondent who entered a branch via a
    // jump to fall through into the next sibling branch's follow-up
    // question. We now read the question's own `next_step`:
    //   - branching question  → use the SELECTED choice.next_step
    //   - non-branching       → use q.next_step (defaults to linear next)
    const resolveNextIndex = (qIdx, answer) => {
        const q = questions[qIdx];
        if (!q) return { kind: 'end' };
        const linearNext = () => {
            const nextIdx = qIdx + 1;
            return nextIdx >= questions.length ? { kind: 'end' } : { kind: 'go', idx: nextIdx };
        };
        const applyNs = (ns) => {
            if (!ns || ns.type === 'next') return linearNext();
            if (ns.type === 'end') return { kind: 'end' };
            if (ns.type === 'jump' && ns.target_question_id) {
                const targetIdx = questions.findIndex((qq) => qq.id === ns.target_question_id);
                if (targetIdx < 0) return { kind: 'end' };
                return { kind: 'go', idx: targetIdx };
            }
            return linearNext();
        };
        if (q.response_type === 'multiple_choice_branching') {
            const choice = (q.choices || []).find((c) => c.id === answer?.value);
            return applyNs(choice?.next_step);
        }
        return applyNs(q.next_step);
    };

    // ── Answer setters ───────────────────────────────────────
    const setAnswer = (q, value, branchTaken = null, otherText = null) => {
        const newAns = {
            question_id: q.id,
            question_text: q.text?.en || q.id,
            value,
            response_type: q.response_type,
            ...(branchTaken ? { branch_taken: branchTaken } : {}),
            ...(otherText ? { other_text: otherText } : {}),
        };
        const next = { ...answers, [q.id]: newAns };
        setAnswers(next);
        return next;
    };

    // ── Auto-advance for branching answers ───────────────────
    // The respondent clicks an answer → we immediately resolve the
    // next step and either jump or submit. They never see "Submit"
    // for a branching question.
    const onBranchingPick = async (q, choice) => {
        // P_BRANCHING_OTHER_CHOICE_1.0 — a choice that invites the guest's own
        // words must NOT auto-advance. Open the box and wait for Continue.
        if (choice.allows_text) {
            setOtherPick({ questionId: q.id, choiceId: choice.id });
            return;
        }
        const branchTaken = {
            choice_id: choice.id,
            choice_label_en: choice.label?.en || '',
            next_step_type: choice.next_step?.type || 'next',
            target_question_id: choice.next_step?.target_question_id || null,
        };
        const updatedAnswers = setAnswer(q, choice.id, branchTaken);
        const nextStep = resolveNextIndex(currentIdx, updatedAnswers[q.id]);
        await advanceOrFinish(updatedAnswers, nextStep);
    };

    // Continue from an "Other" choice: same routing as any other choice,
    // with the guest's words carried on the answer.
    const onOtherContinue = async (q, choice) => {
        const text = (otherTexts[`${q.id}:${choice.id}`] || '').trim();
        if (!text) return;
        const branchTaken = {
            choice_id: choice.id,
            choice_label_en: choice.label?.en || '',
            next_step_type: choice.next_step?.type || 'next',
            target_question_id: choice.next_step?.target_question_id || null,
            other_text: text,
        };
        const updatedAnswers = setAnswer(q, choice.id, branchTaken, text);
        setOtherPick(null);
        const nextStep = resolveNextIndex(currentIdx, updatedAnswers[q.id]);
        await advanceOrFinish(updatedAnswers, nextStep);
    };

    // ── Step movement (shared by every forward path) ─────────
    // Answers stay in state when the guest goes Back, so nothing they
    // might return to is lost. At submit time the collection is filtered
    // down to the questions actually on the final path — the overall
    // experience answer and photos are never touched.
    const prunedToPath = (coll, path) => {
        const onPath = new Set(path.map((i) => questions[i]?.id).filter(Boolean));
        const stepIds = new Set(questions.map((q) => q.id));
        const out = {};
        Object.entries(coll).forEach(([qid, a]) => {
            if (!stepIds.has(qid) || onPath.has(qid)) out[qid] = a;
        });
        return out;
    };

    const rewardSurvey = !!survey?.closing_page?.reward_card_id;

    const advanceOrFinish = async (updatedAnswers, nextStep) => {
        // Guard infinite loop: a jump back into a question already on this
        // path ends the survey, exactly as before.
        if (nextStep.kind === 'end' || visitedPath.includes(nextStep.idx)) {
            // P_SURVEY_SUBMIT_ON_LAST_1.0 — the last question the respondent
            // actually reaches carries Submit itself. Picking an answer never
            // sends and never jumps: the answers are held on the device and
            // only Submit/Finish releases them, so alerts still fire once.
            setPendingAnswers(prunedToPath(updatedAnswers, visitedPath));
            return;
        }
        setPendingAnswers(null);
        setCurrentIdx(nextStep.idx);
        setVisitedPath([...visitedPath, nextStep.idx]);
    };

    const onBack = () => {
        if (visitedPath.length < 2) return;
        setPendingAnswers(null);
        const prev = visitedPath.slice(0, -1);
        setVisitedPath(prev);
        setCurrentIdx(prev[prev.length - 1]);
        setOtherPick(null);
    };

    const onFinish = async () => {
        if (submitting) return;  // one tap, one response
        await submitAll(pendingAnswers || prunedToPath(answers, visitedPath));
    };

    // ── "Next" button (non-branching step-by-step) ───────────
    const onNextStep = async () => {
        const q = questions[currentIdx];
        const ans = answers[q.id];
        if (q.required && (ans === undefined || ans?.value === undefined || ans?.value === '')) {
            return;
        }
        const nextStep = resolveNextIndex(currentIdx, ans);
        await advanceOrFinish(answers, nextStep);
    };

    // ── Single-page (legacy) submit ──────────────────────────
    const allRequiredAnswered = (survey.questions || [])
        .filter(q => q.required)
        .every(q => answers[q.id] !== undefined && answers[q.id]?.value !== undefined && answers[q.id]?.value !== '');

    const handleSubmitSinglePage = async () => {
        if (!allRequiredAnswered) return;
        await submitAll(answers);
    };

    // ── Question renderer (shared by both layouts) ───────────
    const renderQuestion = (q, idx) => (
        <div key={q.id} className="space-y-2" data-testid={`public-q-${idx}`}>
            <p className="text-sm font-medium">
                {q.text?.[effectiveLang] || q.text?.en} {q.required && <span className="text-red-500">*</span>}
            </p>
            {q.response_type === 'rating' && (
                <div className="flex gap-2 justify-center">
                    {[1,2,3,4,5].map(n => (
                        <button
                            key={n}
                            type="button"
                            onClick={() => setAnswer(q, n)}
                            aria-label={`${n} stars`}
                            data-testid={`public-q-${idx}-rating-${n}`}
                            className={`p-2 rounded-md transition-all ${answers[q.id]?.value >= n ? 'text-amber-500 dark:text-amber-400 scale-110' : 'text-slate-300 dark:text-slate-600 hover:text-amber-300 dark:hover:text-amber-500'}`}
                        >
                            <Star className="w-8 h-8" fill={answers[q.id]?.value >= n ? 'currentColor' : 'none'} />
                        </button>
                    ))}
                </div>
            )}
            {q.response_type === 'yes_no_neutral' && (
                <div className="flex gap-2 justify-center flex-wrap">
                    {(q.options || ['yes','no','partially']).map(o => (
                        <Button
                            key={o}
                            type="button"
                            variant={answers[q.id]?.value === o ? 'default' : 'outline'}
                            onClick={() => setAnswer(q, o)}
                            data-testid={`public-q-${idx}-choice-${o}`}
                        >
                            {t(`surveys.option_${o}`, { defaultValue: o })}
                        </Button>
                    ))}
                </div>
            )}
            {q.response_type === 'free_text' && (
                <Textarea
                    value={answers[q.id]?.value || ''}
                    onChange={(e) => setAnswer(q, e.target.value)}
                    placeholder={t('surveys.yourAnswer')}
                    rows={3}
                    data-testid={`public-q-${idx}-text`}
                />
            )}
            {q.response_type === 'multiple_choice_branching' && (
                <div className="space-y-2">
                    {(q.choices || []).map((c) => {
                        const key = `${q.id}:${c.id}`;
                        const typing = c.allows_text && (
                            (otherPick && otherPick.questionId === q.id && otherPick.choiceId === c.id)
                            || answers[q.id]?.value === c.id
                        );
                        return (
                        <div key={c.id} className="space-y-2">
                            <Button
                                type="button"
                                variant={answers[q.id]?.value === c.id || (otherPick?.questionId === q.id && otherPick?.choiceId === c.id) ? 'default' : 'outline'}
                                onClick={() => onBranchingPick(q, c)}
                                className="w-full justify-start text-left"
                                data-testid={`public-q-${idx}-branch-${c.id}`}
                                disabled={submitting}
                            >
                                {c.label?.[effectiveLang] || c.label?.en || ''}
                            </Button>
                            {typing && (
                                <div className="space-y-2 pl-1" data-testid={`public-q-${idx}-other-box-${c.id}`}>
                                    <Textarea
                                        value={otherTexts[key] || ''}
                                        onChange={(e) => setOtherTexts({ ...otherTexts, [key]: e.target.value.slice(0, 300) })}
                                        placeholder={t('surveys.branching.otherPlaceholder', 'Type your answer')}
                                        maxLength={300}
                                        rows={3}
                                        data-testid={`public-q-${idx}-other-text-${c.id}`}
                                    />
                                    <Button
                                        type="button"
                                        onClick={() => onOtherContinue(q, c)}
                                        disabled={submitting || !(otherTexts[key] || '').trim()}
                                        className="w-full"
                                        data-testid={`public-q-${idx}-other-continue-${c.id}`}
                                    >
                                        {t('surveys.branching.otherContinue', 'Continue')}
                                    </Button>
                                </div>
                            )}
                        </div>
                        );
                    })}
                </div>
            )}
        </div>
    );

    // ── Step-by-step layout ──────────────────────────────────
    // P_SURVEY_EDITOR_UX_ENHANCEMENTS_1.0 — platform-level Overall
    // Experience screen. Renders before the first author-defined
    // question for both layouts. Capturing the answer is identical
    // to any other rating question — same shape, same analytics.
    const showOverallScreen =
        survey.overall_experience_enabled !== false
        && !overallDone
        && !submitted;
    if (showOverallScreen) {
        const onPickOverall = (n) => {
            // Record the rating as a regular ResponseAnswer with a
            // reserved question_id, then advance.
            const synth = {
                question_id: OVERALL_QID,
                question_text: 'How was your overall experience?',
                value: n,
                response_type: 'rating',
            };
            setAnswers((prev) => ({ ...prev, [OVERALL_QID]: synth }));
            setOverallDone(true);
        };
        return (
            <BrandingShell
                wallpaperUrl={effectiveWallpaperUrl}
                variant={shellVariant}
                centered={false}
                className="py-6 px-4 sm:px-6"
                data-testid="survey-public-root"
            >
                <ChangeLanguageLink enabled={enabledLangs} onOpen={resetLang} variant="light" />
                <div className="max-w-md mx-auto space-y-4 w-full" data-testid="survey-public-overall-screen">
                    {effectiveLogoUrl && (
                        <div className="flex justify-center pt-2" data-testid="survey-logo-wrapper">
                            <img
                                src={effectiveLogoUrl}
                                alt=""
                                loading="lazy"
                                className={`block object-contain ${LOGO_SIZE_CLASSES[survey.logo_size || 'medium']}`}
                                data-testid="survey-logo-img"
                            />
                        </div>
                    )}
                    <Card className={questionCardSurface} data-card-style={effectiveCardStyle}>
                        <CardHeader>
                            <CardTitle
                                style={{ fontFamily: 'Manrope, sans-serif' }}
                                data-testid="survey-overall-title"
                            >
                                {t('surveys.overallExperience.title', 'How was your overall experience?')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="flex gap-2 justify-center" data-testid="survey-overall-stars">
                                {[1,2,3,4,5].map(n => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => onPickOverall(n)}
                                        aria-label={`${n} stars`}
                                        data-testid={`survey-overall-star-${n}`}
                                        className="p-2 rounded-md transition-all text-slate-300 dark:text-slate-600 hover:text-amber-300 dark:hover:text-amber-500 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-amber-400"
                                    >
                                        <Star className="w-10 h-10" />
                                    </button>
                                ))}
                            </div>
                            <p className="text-xs text-center text-muted-foreground">
                                {t('surveys.overallExperience.hint', 'Tap a star to begin.')}
                            </p>
                        </CardContent>
                    </Card>
                    <p className="text-xs text-center text-muted-foreground">{t('surveys.poweredBy')}</p>
                </div>
            </BrandingShell>
        );
    }

    if (branchingMode) {
        const q = questions[currentIdx];
        if (!q) {
            // Safety: out-of-bounds → submit what we have.
            // This should never happen but better than a white screen.
            handleSubmitSinglePage();
            return null;
        }
        const isBranching = q.response_type === 'multiple_choice_branching';
        // P_SURVEY_SUBMIT_ON_LAST_1.0 — this is the last question on THIS
        // branch when the answer now held leads nowhere further. Derived, so
        // changing the answer re-evaluates the path immediately.
        const answeredHere = answers[q.id] !== undefined
            && answers[q.id]?.value !== undefined && answers[q.id]?.value !== '';
        const liveNext = answeredHere ? resolveNextIndex(currentIdx, answers[q.id]) : null;
        const endsHere = !!liveNext
            && (liveNext.kind === 'end' || visitedPath.includes(liveNext.idx))
            && !otherPick;
        return (
            <BrandingShell
                wallpaperUrl={effectiveWallpaperUrl}
                variant={shellVariant}
                centered={false}
                className="py-6 px-4 sm:px-6"
                data-testid="survey-public-root"
            >
                <ChangeLanguageLink enabled={enabledLangs} onOpen={resetLang} variant="light" />
                <div className="max-w-md mx-auto space-y-4 w-full" data-testid="survey-public-page">
                    {effectiveLogoUrl && (
                        <div className="flex justify-center pt-2" data-testid="survey-logo-wrapper">
                            <img
                                src={effectiveLogoUrl}
                                alt=""
                                loading="lazy"
                                className={`block object-contain ${LOGO_SIZE_CLASSES[survey.logo_size || 'medium']}`}
                                data-testid="survey-logo-img"
                            />
                        </div>
                    )}
                    <Card className={questionCardSurface} data-card-style={effectiveCardStyle}>
                        <CardHeader>
                            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {survey.name?.[effectiveLang] || survey.name?.en || 'Survey'}
                            </CardTitle>
                            <p
                                className="text-xs text-muted-foreground"
                                data-testid="survey-step-indicator"
                            >
                                {t('surveys.stepLabel', { current: visitedPath.length, defaultValue: 'Step {{current}}' })}
                            </p>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {renderQuestion(q, currentIdx)}

                            {currentIdx === questions.length - 1 && PhotoControl()}

                            {endsHere ? (
                                <Button
                                    className="w-full h-12 text-base"
                                    onClick={onFinish}
                                    disabled={submitting}
                                    data-testid={rewardSurvey ? 'public-finish-btn' : 'public-submit-btn'}
                                >
                                    {submitting ? t('common.saving') : (rewardSurvey
                                        ? t('surveys.finish', 'Finish')
                                        : t('surveys.confirmSubmit', 'Submit'))}
                                </Button>
                            ) : !isBranching && (
                                <Button
                                    className="w-full"
                                    onClick={onNextStep}
                                    disabled={submitting || (q.required && (answers[q.id] === undefined || answers[q.id]?.value === undefined || answers[q.id]?.value === ''))}
                                    data-testid="public-step-next-btn"
                                >
                                    {submitting ? t('common.saving') : (
                                        <>
                                            {t('surveys.continue', 'Continue')}
                                            <ChevronRight className="w-4 h-4 ml-1" />
                                        </>
                                    )}
                                </Button>
                            )}

                            {/* Back on every question after the first. */}
                            {visitedPath.length > 1 && (
                                <Button
                                    variant="outline"
                                    className="w-full h-12 text-base"
                                    onClick={onBack}
                                    disabled={submitting}
                                    data-testid="public-back-btn"
                                >
                                    <ChevronLeft className="w-4 h-4 mr-1" />
                                    {t('common.back', 'Back')}
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                    <p className="text-xs text-center text-muted-foreground">{t('surveys.poweredBy')}</p>
                </div>
            </BrandingShell>
        );
    }

    // ── Legacy single-page layout (no branching question present) ──
    return (
        <BrandingShell
            wallpaperUrl={effectiveWallpaperUrl}
            variant={shellVariant}
            centered={false}
            className="py-6 px-4 sm:px-6"
            data-testid="survey-public-root"
        >
            <ChangeLanguageLink enabled={enabledLangs} onOpen={resetLang} variant="light" />
            <div className="max-w-md mx-auto space-y-4 w-full" data-testid="survey-public-page">
                {effectiveLogoUrl && (
                    <div className="flex justify-center pt-2" data-testid="survey-logo-wrapper">
                        <img
                            src={effectiveLogoUrl}
                            alt=""
                            loading="lazy"
                            className={`block object-contain ${LOGO_SIZE_CLASSES[survey.logo_size || 'medium']}`}
                            data-testid="survey-logo-img"
                        />
                    </div>
                )}
                <Card className={questionCardSurface} data-card-style={effectiveCardStyle}>
                    <CardHeader>
                        <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {survey.name?.[effectiveLang] || survey.name?.en || 'Survey'}
                        </CardTitle>
                        {survey.description?.[effectiveLang] && (
                            <p className="text-sm text-muted-foreground">{survey.description[effectiveLang]}</p>
                        )}
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {(survey.questions || []).map((q, idx) => renderQuestion(q, idx))}

                        {PhotoControl()}

                        <div className="space-y-2">
                            <p className="text-sm font-medium">{t('surveys.notesLabel')}</p>
                            <Textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder={t('surveys.notesPlaceholder')}
                                rows={3}
                                data-testid="public-notes-input"
                            />
                        </div>

                        <Button
                            className="w-full"
                            onClick={handleSubmitSinglePage}
                            disabled={submitting || !allRequiredAnswered}
                            data-testid="public-submit-btn"
                        >
                            {submitting ? t('common.saving') : t('surveys.submit')}
                        </Button>
                    </CardContent>
                </Card>
                <p className="text-xs text-center text-muted-foreground">{t('surveys.poweredBy')}</p>
            </div>
        </BrandingShell>
    );
};

export default SurveyPublic;
